import React, { useEffect, useState, useMemo, useRef } from "react";
import SideBar from "./Sidebar";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  MaterialReactTable,
  useMaterialReactTable,
  MRT_TablePagination,
  MRT_ToggleFullScreenButton,
  MRT_ToggleDensePaddingButton,
} from "material-react-table";
import * as moment from "moment";
import axios from "../axios";
import { NavLink } from "react-router-dom";
import { Box, Button, IconButton } from "@mui/material";
import { DateRangePickerComponent } from "@syncfusion/ej2-react-calendars";
import { registerLicense } from "@syncfusion/ej2-base";
import Navbar from "./Navbar";
import Topbar from "./Topbar";
registerLicense(
  "ORg4AjUWIQA/Gnt2UVhhQlVFfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hTX5RdEVjXntdcHNVQGda;MzExNjY5OUAzMjM0MmUzMDJlMzBFZmo4T0hTeUlZQjB3cjhSVFkvT0tLUDFUVEtMZU9NQXljVHk3dXloYnJ3PQ=="
);

const Assessments = React.memo(() => {
  let date = moment(new Date());
  const [initialRender, setIntialRender] = useState(true);
  const [firstSortedCall, setFirstSortedCall] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [prevPageIndex, setPrevPageIndex] = useState(1);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [sortingState, setSortingState] = useState(true);
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 50,
  });
  const [pageNumber, setPageNumber] = useState(1);
  // const [endDate, setEndDate] = useState(
  //   new Date(date.add(1, "day").valueOf())
  // );
  // const [startDate, setStartDate] = useState(
  //   new Date(date.subtract(1, "month").valueOf())
  // );

  const [dateRange, setDateRange] = useState({
    startDate: new Date(date.subtract(1, "week").valueOf()),
    endDate: new Date(date.add(7, "day").valueOf()),
  });

  const [data, setData] = useState([]);
  const [pageSettings, setPageSettings] = useState({
    pageSize: 20, // Set your desired page size here
    pageCount: 0,
  });
  const [filterSettings, setFilterSettings] = useState({});
  const [sortSettings, setSortSettings] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [dates1, setDates1] = useState("");
  const [dates2, setDates2] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateRangePickerRef = useRef(null);
  const [sortField, setSortField] = useState("");
  const [sortType, setSortType] = useState("desc");

  const handleFilterChange = (args) => {
    setFilterSettings(args.currentFilterObject);
  };

  const handleSortChange = (args) => {
    const { currentSortObject } = args;
    const field = currentSortObject[0]?.id || "";
    const type = currentSortObject[0]?.desc ? "desc" : "asc";
    setSortField(field);
    setSortType(type);
  };

  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
  };
  const darkTheme = createTheme({
    palette: {
      // mode: 'dark',
      mode: "light",
    },
  });
  // const riskList = ["Normal", "Abnormla"];
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: "patientDetails.patientId",
        header: "ID",
        enableSorting: false,
        Cell: ({ cell }) => {
          return (
            <NavLink
              className="table__row__item"
              target="_blank"
              to={`patient/${cell.row.original.patientDetails.userId}/${encodeURIComponent(cell.getValue())}`}
            >
              {cell.getValue() || "-"}
            </NavLink>
          );
        },
      },
      {
        accessorKey: "patientDetails.firstname",
        header: "Name",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      }
    ];
  
    // Conditionally include Lung Health Index if localStorage.lhi is true
    if (localStorage.lhi === "true") {
      baseColumns.push({
        accessorKey: "lung_health_index",
        header: "Lung Health Index",
        enableSorting: false,
        Cell: ({ cell }) => {
          let textValue = "";
          if (cell.getValue() === "low") {
            textValue = "Low";
          }
          return textValue || cell.getValue() || "-";
        },
      });
    }
  
    // If patientPayment is true, return only ID, Name, and Lung Health Index
    if (localStorage.patientPayment === "true") {
      return baseColumns;
    }
  
    // Include other columns when patientPayment is not true
    baseColumns.push(
      {
        accessorKey: "risk",
        header: "Underlying Risk",
        enableSorting: false,
        Cell: ({ cell }) => {
          let textValue = "";
          if (cell.row.original.risk === "Yes") {
            textValue = "Abnormal";
          } else if (cell.row.original.risk === "No") {
            textValue = "Normal";
          }
          return <span>{textValue || "-"}</span>;
        },
      },
      {
        accessorKey: "spirometry",
        header: "Pattern",
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "severity",
        header: "Pattern Severity",
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "dry_sequence_count",
        header: "Dry Cough Count",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "wet_sequence_count",
        header: "Wet Cough Count",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      }
    );
  
    // Add doctor ID if validation and sharedPatient or role is admin
    if (localStorage.validation === "true" && localStorage.sharedPatient === "true" || localStorage.role === "admin") {
      baseColumns.push({
        accessorKey: "userId",
        header: "Doctor Id",
        enableSorting: false,
        enableColumnFilter: true,
      });
    }
  
    // Add TB Risk and TB Likely columns if Tb is set to "tb"
    if (localStorage.Tb === "tb") {
      baseColumns.push(
        {
          accessorKey: "tb_Risk",
          header: "TB Risk",
          enableSorting: false,
          Cell: ({ cell }) => cell.getValue() || "-",
        },
        {
          accessorKey: "tb_likely",
          header: "TB Likely",
          enableSorting: false,
          enableColumnFilter: false,
          Cell: ({ cell }) => cell.getValue() || "-",
        }
      );
    }
  
    return baseColumns;
  }, [localStorage.patientPayment, localStorage.lhi, localStorage.validation, localStorage.Tb]);
  
  const table = useMaterialReactTable({
    columns,
    enableCellActions: true,
    muiPaginationProps: {
      showRowsPerPage: true,
      rowsPerPageOptions: [5, 20, 50, 100],
      showFirstButton: false,
      showLastButton: false,
    },

    muiTableProps: {
      sx: {
        borderCollapse: "collapse",
        "& td": {
          border: "none",
          fontSize: "14px",
        },
        "& th": {
          borderRight: "2px solid #a9a9a9",
          // borderLeft: '1px solid #606060',
          // margin:'2px 0 2px 0'
        },
      },
    },
    muiSkeletonProps: {
      animation: "wave",
    },

    muiTableHeadCellProps: {
      sx: {
        display: "flex",
        justifyContent: "center",
        fontSize: "14px",
        backgroundColor: "#fdfaf0",
        minHeight: "40px",
        borderRight: "1px solid #000",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: "12px",
      },
    },
    muiTopToolbarProps: {
      sx: {
        boxSizing: "12px",
        fontSize: "14px",
      },
    },
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    muiTableContainerProps: { sx: { maxHeight: "600px" } },
    data,
    manualFiltering: true,
    manualSorting: true,
    enableGlobalFilter: false,
    enableSortingRemoval: true,
    enableDensityToggle: true,
    manualGrouping: true,
    pageSize: pageSize,
    manualPagination: true,
    enableFilterMatchHighlighting: false,
    // enableStickyHeader: true,
    // enableStickyFooter: true,
    // enableFilterMatchHighlighting: true,

    columnFilterDisplayMode: "popover",
    enableColumnFilterModes: true,
    enableFacetedValues: true,
    enableFilters: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    enableFullScreenToggle: true,
    enableColumnActions: true,

    initialState: { showColumnFilters: true },

    renderBottomToolbar: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingLeft: "10px",
          // fontSize:'30px',
          // fontWeight: 'bold'
        }}
      >
        <MRT_TablePagination table={table} />
      </Box>
    ),
    manualSorting: true,
    sorting: sorting,
    onSortingChange: setSorting,

    isMultiSortEvent: () => false,

    onColumnFiltersChange: setColumnFilters,
    // onColumnFiltersChange: handleFilterChange,

    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    // onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  const queryParams = columnFilters.reduce((params, filter) => {
    if (filter.id === "patientDetails.patientId") {
      params["patientId"] = filter.value;
    } else if (filter.id === "lung_health_index") {
      params["lhi"] = filter.value;
    } else if (filter.id === "dry_sequence_count") {
      params["dry_cough_count"] = filter.value;
    } else if (filter.id === "userId") {
      params["userId"] = filter.value;
    } else if (filter.id === "wet_sequence_count") {
      params["wet_cough_count"] = filter.value;
    } else if (filter.id === "tb_Risk") {
      params["tb_risk"] = filter.value;
    } else {
      params[filter.id] = filter.value;
    }
    return params;
  }, {});

  const sortingParams = (sorting) => {
    const params = {};

    // Check if sorting information is provided
    if (sorting && sorting.length > 0) {
      const sortInfo = sorting[0];
      if (sortInfo.id === "patientDetails.firstname") {
        params["sort_field"] = "firstname";
        params["sort_type"] = sortInfo.desc ? "desc" : "asc";
      } else {
        params["sort_field"] = sortInfo.id;
        params["sort_type"] = sortInfo.desc ? "desc" : "asc";
      }
    }

    return params;
  };

  useEffect(() => {
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    if (initialRender) {
      fetchData();
      setDates1(startDate1.toLocaleString());
      setDates2(endDate1.toLocaleString());
      setIntialRender(false);
    }
  }, []);

  useEffect(() => {
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    if (!initialRender) {
      fetchData();
      setDates1(startDate1.toLocaleString());
      setDates2(endDate1.toLocaleString());
      setIntialRender(false);
    }
  }, [dateRange.startDate, dateRange.endDate]);
  const fetchData = async () => {
    // console.log("filters", JSON.stringify(columnFilters ?? []),);
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    try {
      const response = await axios.get(
        `/records/?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59&successful_assessments=true`,
        {
          headers: {
            accessToken: localStorage.accessToken,
          },
          params: {
            page_size: pagination.pageSize,
            page_number: pageNumber,
          },
        }
      );

      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.records);
        setRowCount(response.data.total_records);
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }

    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  useEffect(() => {
    // Fetch paginated data whenever pagination changes
    if (initialRender) {
    } else {
      fetchPaginatedData();
    }
  }, [pagination.pageIndex]);
  const fetchPaginatedData = async () => {
    const nextPageIndex = pagination.pageIndex + 1;
    if (nextPageIndex !== prevPageIndex) {
      if (nextPageIndex > prevPageIndex) {
        // setPageNumber(nextPageIndex);
        fetchNextpatients(nextPageIndex);
        console.log(nextPageIndex);
      } else if (nextPageIndex < prevPageIndex) {
        // setPageNumber(nextPageIndex);
        fetchPreviouspatients(nextPageIndex);
        // console.log(nextPageIndex);
      }
      setPrevPageIndex(nextPageIndex);
    }
  };
  const fetchNextpatients = async (ind) => {
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await axios.get(
        `/records/?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59&successful_assessments=true`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_number: ind,
            page_size: pagination.pageSize,
            ...queryParams,
            ...sortingParams(sorting),
          },
        }
      );
      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.records);
        // setRowCount(response.data.total_records);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (error) {
      console.log("error");
    }
  };

  const fetchPreviouspatients = async (ind) => {
    // Constructing query parameters from columnFilters
    setPageNumber((prevState) => prevState - 1);
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await axios.get(
        `/records/?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59&successful_assessments=true`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_number: ind,
            page_size: pagination.pageSize,
            ...queryParams,
            ...sortingParams(sorting),
          },
        }
      );
      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.records);
        // setRowCount(response.data.total_records);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (error) {
      console.log("error");
    }
  };

  useEffect(() => {
    // Fetch initial data when component mounts
    if (initialRender) {
    } else {
  
      // fetchData();
      fetchPageSizeData();
    }
  }, [pagination.pageSize]);

  const fetchPageSizeData = async () => {
    // console.log("i ran");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    try {
      const response = await axios.get(
        `/records/?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59&successful_assessments=true`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_size: pagination.pageSize,
            ...sortingParams(sorting),
            ...queryParams,
          },
        }
      );
      if (response.data.message) {
        setData([]);
        // console.log("no data");
      } else {
        setData(response.data.records);
        // console.log("patients list", response.data.records);
        if (firstSortedCall) {
          setFirstSortedCall(false);
        }
        setRowCount(response.data.total_records);
      }
    } catch (error) {
      setIsError(true);
      console.error(error);
      return;
    }
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  useEffect(() => {
    if (initialRender) {
    } else {
      // setSortingState((prevState) => !prevState);
      fetchFilteredData();
      console.log(columnFilters);
    }

    // console.log("sorting called");
  }, [columnFilters]);
  const fetchFilteredData = async () => {
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    try {
      const response = await axios.get(
        `/records/?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59&successful_assessments=true`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            ...queryParams,
            page_size: pagination.pageSize,
            ...sortingParams(sorting),
            // patientId: "AA3SRAV027"
          },
        }
      );

      if (response.data.message) {
        setData([]);
      } else {
        setData(response.data.records);
        setRowCount(response.data.total_records);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    } catch (error) {
      console.log("error", error);
      setIsError(true);
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  useEffect(() => {
    if (!initialRender) {
      fetchSortedData();
    }
  }, [sorting]);

  const fetchSortedData = async () => {
    const startDate1 = moment(dateRange.startDate).format("YYYY-MM-DD");
    const endDate1 = moment(dateRange.endDate).format("YYYY-MM-DD");
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    await axios
      .get(
        `/records/?created_on_start=${startDate1} 00:00:00&created_on_end=${endDate1} 23:59:59&successful_assessments=true`,
        {
          headers: { accessToken: localStorage.accessToken },
          params: {
            page_size: pagination.pageSize,
            ...sortingParams(sorting),
            ...queryParams,
          },
        }
      )
      .then((response) => {
        setData(response.data.records);
        setRowCount(response.data.total_records);
      })
      .catch((error) => {
        console.log("error", error);
      });
    setIsError(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  const handleClearFilters = () => {
    setColumnFilters([]);
    setGlobalFilter("");
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setSelectedOption(value);

    // Call respective handler functions based on selected option
    if (value === "1 week") {
      oneWeekHandler();
      setShowDatePicker(false);
    } else if (value === "1 day") {
      oneDayHandler();
      setShowDatePicker(false);
    } else if (value === "1 month") {
      oneMonthHandler();
      setShowDatePicker(false);
    } else if (value === "1 year") {
      oneYearHandler();
      setShowDatePicker(false);
    } else if (value === "custom") {
      setShowDatePicker(true);
    }
  };

  const oneWeekHandler = () => {
    let newStartdate = moment(new Date());
    let newEnddate = moment(new Date());
    // setStartDate(new Date(newStartdate.subtract(7, "day").valueOf()));
    // setEndDate(new Date(newEnddate.add(1, "day").valueOf()));
    setDateRange({
      startDate: new Date(newStartdate.subtract(7, "day").valueOf()),
      endDate: new Date(newEnddate.add(1, "day").valueOf()),
    });

    setSelectedOption("1 week");
  };
  const oneMonthHandler = () => {
    let newStartdate = moment(new Date());
    let newEnddate = moment(new Date());
    // setStartDate(new Date(newStartdate.subtract(1, "month").valueOf()));
    // setEndDate(new Date(newEnddate.add(1, "day").valueOf()));
    setDateRange({
      startDate: new Date(newStartdate.subtract(1, "month").valueOf()),
      endDate: new Date(newEnddate.add(1, "day").valueOf()),
    });

    setSelectedOption("1 month");
  };
  const oneDayHandler = () => {
    setSelectedOption("1 day");
    const today = moment().startOf("day").toDate();
    // setStartDate(today); // Set start date to today
    // setEndDate(today); // Set end date to today
    setDateRange({ startDate: today, endDate: today });
  };
  const oneYearHandler = () => {
    setSelectedOption("1 year");
    const startDate = moment().subtract(1, "year").toDate();
    const endDate = moment().toDate();
    // setStartDate(startDate); // Set start date 1 year ago
    // setEndDate(endDate);
    setDateRange({ startDate: startDate, endDate: endDate }); // Set end date to end of this year
  };

  const handleRangeSelected = (args) => {

    const startDate1 = args.value[0];
    const endDate1 = args.value[1];

    const startDate2 = moment(startDate1).format("YYYY-MM-DD");
    const endDate2 = moment(endDate1).format("YYYY-MM-DD");
    setDateRange({ startDate: startDate2, endDate: endDate2 });
    // setStartDate(startDate2);
    // setEndDate(endDate2);
    setSelectedOption("");
  };
  const handleDateRangePickerRef = (ref) => {
    if (ref && selectedOption === "custom") {
      ref.show();
    }
  };

  const [width, setWidth] = React.useState(window.innerWidth);
  const breakpoint = 1000;
  React.useEffect(() => {
    const handleResizeWindow = () => setWidth(window.innerWidth);
    // subscribe to window resize event "onComponentDidMount"
    window.addEventListener("resize", handleResizeWindow);
    return () => {
      // unsubscribe "onComponentDestroy"
      window.removeEventListener("resize", handleResizeWindow);
    };
  }, []);
  

  return (
    <>
      <div className="dashboard">
        <div className="dashboard__content">
          <div className="patients__filters">
            <div className="dateSelection">
              <select
                className="selectDate"
                id="duration"
                value={selectedOption}
                onChange={handleChange}
              >
                <option>Date Range</option>
                <option value="1 day">1 Day</option>
                <option value="1 week">1 Week</option>
                <option value="1 month">1 Month</option>
                <option value="1 year">1 Year</option>
                <option value="custom" key="">
                  Custom
                </option>
              </select>
              {showDatePicker && (
                <div className="dateRangePicker">
                  <DateRangePickerComponent
                    id="daterangepicker"
                    placeholder="Select a range"
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    showClearButton={false}
                    onChange={handleRangeSelected}
                    ref={(ref) => handleDateRangePickerRef(ref)}
                  />
                </div>
              )}
              <div className="dates-container">
                <p className="dates">{moment(dates1).format("DD-MM-YYYY")} </p>
                <p className="dates">to</p>
                <p className="dates">{moment(dates2).format("DD-MM-YYYY")} </p>
              </div>
            </div>
            <ThemeProvider theme={darkTheme}>
              <CssBaseline />
              <MaterialReactTable
                table={table}
                pageSize={pagination.pageSize}
              />
            </ThemeProvider>
          </div>
        </div>
      </div>
    </>
  );
});

export default Assessments;
